import { colors } from '@fortum/elemental-ui';
import { logException } from '@configs/azureAppInsights/utils';
import { addHeader, pages, customerInfoSingle } from '@utils/pdfUtils';
import { DeliveryPoint } from '@models/deliveryPoint';
import { Namespace, useTranslation } from 'react-i18next';
import { transformDeliveryPoint } from '@pages/authenticated/common/HeatingConsumptionVisualization/utils';

const INK_GREY_RGB = 51;

export const usePdfDownloader = () => {
  const { t } = useTranslation<Namespace>('glossary');

  const downloadAsPDF = async (
    hiddenChartRef: React.RefObject<HTMLDivElement>,
    title: string,
    filename: string,
    isMobileScreen: boolean,
    userName: string,
    deliveryPoint: DeliveryPoint,
    loadingCallback?: (loading: boolean) => void,
  ) => {
    const { jsPDF } = await import('jspdf');
    try {
      await import('@assets/fonts/FortumSansTT-Regular-normal');
      await import('../../assets/fonts/FortumSansTT-Bold-bold');

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({ orientation: 'landscape' });

      pdf.setFont('FortumSansTT-Regular', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(INK_GREY_RGB);

      addHeader(pdf);
      const transformedDeliveryPoint = transformDeliveryPoint(
        userName,
        deliveryPoint,
      );
      customerInfoSingle(pdf, t, userName, transformedDeliveryPoint);
      pdf.setFont('FortumSansTT-Bold', 'bold');
      pdf.text(title, 10, 80);

      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;

      const chartElement = hiddenChartRef.current;
      if (!chartElement) {
        throw new Error('No hidden graph to generate image.');
      }

      const originalStyles = {
        position: chartElement.style.position,
        backgroundColor: chartElement.style.backgroundColor,
        bottom: chartElement.style.bottom,
      };

      chartElement.style.position = 'absolute';
      chartElement.style.backgroundColor = colors.snowWhite;
      chartElement.style.bottom = '0';

      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(chartElement, {
        scale: 1,
        useCORS: true,
        width: 1200,
        height: 550,
      });

      Object.assign(chartElement.style, originalStyles);

      const chartImage = canvas.toDataURL('image/jpeg', 0.65);

      const chartYPosition = 90;
      const chartHeight = pageHeight - chartYPosition;

      pdf.addImage(
        chartImage,
        'JPEG',
        10,
        chartYPosition,
        pageWidth - 20,
        chartHeight,
      );

      pages(pdf);

      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      loadingCallback?.(false);
    } catch (err) {
      logException(`Error while downloading as PDF. ${err}`);
      loadingCallback?.(false);
    }
  };

  return { downloadAsPDF };
};
